import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Loader2, Search, ChevronLeft, XCircle, AlertTriangle, History, RotateCcw } from "lucide-react";
import StornoDialog from "./StornoDialog";

const eur = (n) => `€ ${(Number(n) || 0).toFixed(2)}`;
const fmt = (s) => (s ? new Date(s).toLocaleString("it-IT") : "—");

const ORDER_STATUS = { submitted: "Inviato", accepted: "Accettato", preparing: "In preparazione", ready: "Pronto", served: "Servito", closed: "Chiuso", cancelled: "Annullato", draft: "Bozza" };
const PAY_STATUS = { unpaid: "Non pagato", pay_at_counter: "Alla cassa", payment_pending: "In attesa", paid: "Pagato", failed: "Fallito", refunded: "Rimborsato" };

const EMPTY = { date_from: "", date_to: "", order_code: "", table_id: "", source: "", status: "", payment_status: "", payment_method: "", operator: "", voided: "" };

export default function OrderHistoryDialog({ open, onOpenChange, tables = [] }) {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [filters, setFilters] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);   // full order
  const [events, setEvents] = useState([]);
  const [stornoTarget, setStornoTarget] = useState(null);
  const [returnOpen, setReturnOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/orders-history", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line

  const openDetail = async (o) => {
    setDetail(o); setEvents([]);
    try { const { data } = await api.get(`/orders/${o.id}/events`); setEvents(data); } catch { /* noop */ }
  };
  const refreshAfterStorno = async () => {
    load();
    if (detail?.id) { try { const { data } = await api.get(`/orders/${detail.id}`); setDetail(data); } catch { /* noop */ } }
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[97vw] max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="order-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5" /> Storico ordini</DialogTitle>
          <DialogDescription className="text-xs">Filtra e consulta tutti gli ordini. Clicca una riga per il dettaglio e la timeline.</DialogDescription>
        </DialogHeader>

        {!detail ? (
          <>
            {/* FILTERS */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 rounded-xl border border-border p-3">
              <div className="space-y-1"><Label className="text-[11px]">Dal</Label><Input type="date" className="h-9" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="oh-date-from" /></div>
              <div className="space-y-1"><Label className="text-[11px]">Al</Label><Input type="date" className="h-9" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="oh-date-to" /></div>
              <div className="space-y-1"><Label className="text-[11px]">Numero ordine</Label><Input className="h-9" value={filters.order_code} onChange={(e) => set("order_code", e.target.value)} placeholder="DG-…" data-testid="oh-code" /></div>
              <div className="space-y-1"><Label className="text-[11px]">Tavolo</Label>
                <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={filters.table_id} onChange={(e) => set("table_id", e.target.value)} data-testid="oh-table">
                  <option value="">Tutti</option>
                  {tables.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="space-y-1"><Label className="text-[11px]">Origine</Label>
                <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={filters.source} onChange={(e) => set("source", e.target.value)} data-testid="oh-source">
                  <option value="">Tutte</option><option value="staff">Staff</option><option value="qr">QR / Cliente</option><option value="ai">AI</option>
                </select>
              </div>
              <div className="space-y-1"><Label className="text-[11px]">Stato ordine</Label>
                <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={filters.status} onChange={(e) => set("status", e.target.value)} data-testid="oh-status">
                  <option value="">Tutti</option>{Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="space-y-1"><Label className="text-[11px]">Stato pagamento</Label>
                <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={filters.payment_status} onChange={(e) => set("payment_status", e.target.value)} data-testid="oh-paystatus">
                  <option value="">Tutti</option>{Object.entries(PAY_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="space-y-1"><Label className="text-[11px]">Metodo pagamento</Label>
                <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={filters.payment_method} onChange={(e) => set("payment_method", e.target.value)} data-testid="oh-paymethod">
                  <option value="">Tutti</option><option value="cash">Contanti</option><option value="card">Carta</option><option value="sumup">SumUp</option><option value="other">Altro</option>
                </select>
              </div>
              <div className="space-y-1"><Label className="text-[11px]">Operatore</Label><Input className="h-9" value={filters.operator} onChange={(e) => set("operator", e.target.value)} placeholder="Nome/email" data-testid="oh-operator" /></div>
              <div className="space-y-1"><Label className="text-[11px]">Stornato</Label>
                <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={filters.voided} onChange={(e) => set("voided", e.target.value)} data-testid="oh-voided">
                  <option value="">Tutti</option><option value="no">Non stornati</option><option value="yes">Solo stornati</option>
                </select>
              </div>
              <div className="flex items-end gap-2 col-span-2 sm:col-span-1">
                <Button className="rounded-full h-9" onClick={load} disabled={loading} data-testid="oh-apply"><Search className="w-4 h-4 mr-1" /> Filtra</Button>
                <Button variant="ghost" className="rounded-full h-9" onClick={() => { setFilters(EMPTY); }} data-testid="oh-reset">Azzera</Button>
              </div>
            </div>

            {/* TABLE */}
            <div className="mt-3 overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm min-w-[860px]" data-testid="oh-table-grid">
                <thead className="bg-secondary text-muted-foreground">
                  <tr className="text-left">
                    <th className="p-2 font-medium">Ordine</th><th className="p-2 font-medium">Data/ora</th><th className="p-2 font-medium">Tavolo</th>
                    <th className="p-2 font-medium text-right">Totale</th><th className="p-2 font-medium">Stato</th><th className="p-2 font-medium">Pagamento</th>
                    <th className="p-2 font-medium">Metodo</th><th className="p-2 font-medium">Fiscale</th>
                    <th className="p-2 font-medium text-right">Food cost</th><th className="p-2 font-medium text-right">Margine</th><th className="p-2 font-medium">Origine</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={11} className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-muted-foreground" /></td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={11} className="p-8 text-center text-muted-foreground" data-testid="oh-empty">Nessun ordine con questi filtri.</td></tr>
                  ) : rows.map((o) => (
                    <tr key={o.id} className="border-t border-border hover:bg-secondary/50 cursor-pointer" onClick={() => openDetail(o)} data-testid={`oh-row-${o.id}`}>
                      <td className="p-2 font-medium whitespace-nowrap">{o.order_code}{o.is_voided && <Badge className="ml-1 rounded-full bg-red-100 text-red-700 text-[10px]">STORNATO</Badge>}</td>
                      <td className="p-2 whitespace-nowrap text-muted-foreground">{fmt(o.created_at)}</td>
                      <td className="p-2 whitespace-nowrap">{o.table_number || "—"}</td>
                      <td className="p-2 text-right whitespace-nowrap">{eur(o.total)}</td>
                      <td className="p-2 whitespace-nowrap">{ORDER_STATUS[o.status] || o.status}</td>
                      <td className="p-2 whitespace-nowrap">{PAY_STATUS[o.payment_status] || o.payment_status || "—"}</td>
                      <td className="p-2 whitespace-nowrap">{o.payment_method || "—"}</td>
                      <td className="p-2 whitespace-nowrap text-muted-foreground">{o.fiscal_status || "—"}</td>
                      <td className="p-2 text-right whitespace-nowrap">{eur(o.food_cost)}</td>
                      <td className="p-2 text-right whitespace-nowrap">{eur(o.theoretical_margin)}</td>
                      <td className="p-2 whitespace-nowrap">{o.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          /* DETAIL */
          <div data-testid="oh-detail">
            <Button variant="ghost" size="sm" className="rounded-full mb-2" onClick={() => setDetail(null)} data-testid="oh-back"><ChevronLeft className="w-4 h-4 mr-1" /> Torna allo storico</Button>

            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <div>
                <h3 className="font-dash text-xl font-semibold">{detail.order_code}</h3>
                <p className="text-xs text-muted-foreground">{fmt(detail.created_at)} · {detail.table_number ? `Tavolo ${detail.table_number}` : "—"} · {detail.source}</p>
              </div>
              {detail.is_voided && <Badge className="rounded-full bg-red-100 text-red-700" data-testid="oh-voided-badge">ORDINE STORNATO</Badge>}
            </div>

            {detail.is_voided && (
              <div className="mb-3 rounded-lg bg-red-50 text-red-700 p-3 text-sm" data-testid="oh-voided-info">
                <p className="font-medium flex items-center gap-2"><XCircle className="w-4 h-4" /> Storno registrato</p>
                <p>Motivo: «{detail.void_reason || detail.reversal?.reason || "—"}»</p>
                <p>Da: {detail.voided_by || detail.reversed_by || "—"} · {fmt(detail.voided_at || detail.reversed_at)}</p>
                {detail.payment_status === "paid" && (
                  <p className="mt-1 flex items-start gap-1.5 text-amber-800"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> ATTENZIONE: l'ordine è stato stornato ma il pagamento risulta ancora acquisito.</p>
                )}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-border p-3">
                <p className="text-xs uppercase font-semibold text-muted-foreground mb-2">Articoli</p>
                <div className="space-y-1 text-sm">
                  {(detail.items || []).map((i, k) => (
                    <div key={k} className="flex justify-between"><span>{i.quantity}× {i.name}</span><span>{eur((i.unit_price || 0) * (i.quantity || 0))}</span></div>
                  ))}
                  {(!detail.items || detail.items.length === 0) && <p className="text-muted-foreground">Nessun articolo</p>}
                  <div className="flex justify-between border-t border-border pt-1 mt-1 font-semibold"><span>Totale</span><span>{eur(detail.total)}</span></div>
                </div>
                <div className="mt-3 text-sm space-y-1 border-t border-border pt-2">
                  <div className="flex justify-between"><span className="text-muted-foreground">Stato</span><span>{ORDER_STATUS[detail.status] || detail.status}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Pagamento</span><span>{PAY_STATUS[detail.payment_status] || detail.payment_status || "—"}</span></div>
                  {detail.payment_method && <div className="flex justify-between"><span className="text-muted-foreground">Metodo</span><span>{detail.payment_method}</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">Operatore</span><span>{detail.created_by || "—"}</span></div>
                </div>
              </div>

              <div className="rounded-xl border border-border p-3">
                <p className="text-xs uppercase font-semibold text-muted-foreground mb-2">Timeline / audit</p>
                <div className="space-y-2 text-sm max-h-[40vh] overflow-y-auto" data-testid="oh-timeline">
                  {events.length === 0 && <p className="text-muted-foreground">Nessun evento registrato.</p>}
                  {events.map((ev, k) => (
                    <div key={k} className="flex items-start gap-2">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#C9A24B] shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium">{ev.event}</p>
                        <p className="text-xs text-muted-foreground">{fmt(ev.created_at)}{ev.user ? ` · ${ev.user}` : ""}{ev.from_status ? ` · ${ev.from_status}→${ev.to_status}` : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {canManage && detail.status === "closed" && detail.sales_committed && !detail.is_voided && (
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" className="rounded-full" onClick={() => setReturnOpen(true)} data-testid="oh-return-btn">
                  <RotateCcw className="w-4 h-4 mr-1" /> Registra reso
                </Button>
                <Button variant="destructive" className="rounded-full" onClick={() => setStornoTarget(detail)} data-testid="oh-storno-btn">
                  <XCircle className="w-4 h-4 mr-1" /> Storna ordine
                </Button>
              </div>
            )}
          </div>
        )}

        {returnOpen && detail && (
          <ReturnDialog open={returnOpen} onOpenChange={setReturnOpen} order={detail} canManage={canManage} onDone={() => setReturnOpen(false)} />
        )}

        {stornoTarget && (
          <StornoDialog open={!!stornoTarget} onOpenChange={(o) => !o && setStornoTarget(null)} order={stornoTarget} onDone={() => { setStornoTarget(null); refreshAfterStorno(); }} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReturnDialog({ open, onOpenChange, order, canManage, onDone }) {
  const [sel, setSel] = useState({});
  const [reason, setReason] = useState("");
  const [restock, setRestock] = useState(false);
  const [refundMethod, setRefundMethod] = useState("");
  const [busy, setBusy] = useState(false);
  const items = (order.items || []).filter((i) => !i.voided);
  const toggle = (it) => setSel((s) => ({ ...s, [it.item_id]: s[it.item_id] ? undefined : { name: it.name, qty: it.quantity, amount: (i => (i.line_total ?? (i.unit_price || 0) * (i.quantity || 0)))(it), recipe_id: it.recipe_id || "" } }));
  const setQty = (id, q) => setSel((s) => ({ ...s, [id]: { ...s[id], qty: Math.max(1, parseInt(q) || 1) } }));
  const chosen = Object.values(sel).filter(Boolean);
  const submit = async () => {
    if (chosen.length === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    if (!reason.trim()) { toast.error("Motivo obbligatorio"); return; }
    setBusy(true);
    try {
      await api.post("/pos/returns", { order_id: order.id, items: chosen, reason: reason.trim(), restock, refund_method: refundMethod });
      toast.success("Reso registrato" + (restock ? " · magazzino reintegrato" : ""));
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto" data-testid="return-dialog">
        <DialogHeader><DialogTitle>Registra reso · {order.order_code}</DialogTitle>
          <DialogDescription className="text-xs">Reso, rimborso pagamento, reintegro magazzino e annullamento fiscale sono concetti distinti. Nessun rimborso SumUp né annullamento fiscale viene simulato.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.item_id} className={`rounded-lg border p-2 ${sel[it.item_id] ? "border-primary bg-primary/5" : "border-border"}`}>
              <button className="w-full text-left text-sm flex justify-between" onClick={() => toggle(it)} data-testid={`return-item-${it.item_id}`}>
                <span>{it.name}</span><span>{eur(Math.round((it.line_total ?? (it.unit_price || 0) * (it.quantity || 0)) * 100))}</span>
              </button>
              {sel[it.item_id] && (
                <div className="mt-1 flex items-center gap-2"><span className="text-xs text-muted-foreground">Qtà resa</span>
                  <Input className="h-7 w-16" type="number" value={sel[it.item_id].qty} onChange={(e) => setQty(it.item_id, e.target.value)} data-testid={`return-qty-${it.item_id}`} /></div>
              )}
            </div>
          ))}
        </div>
        <div className="space-y-2">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo del reso (obbligatorio)" data-testid="return-reason" />
          <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={refundMethod} onChange={(e) => setRefundMethod(e.target.value)} data-testid="return-refund-method">
            <option value="">Nessun rimborso gestionale</option>
            <option value="cash">Rimborso contanti (registro)</option>
            <option value="card">Rimborso carta (registro)</option>
            <option value="other">Altro (registro)</option>
          </select>
          <label className={`flex items-center gap-2 text-sm ${canManage ? "" : "opacity-50"}`}>
            <input type="checkbox" checked={restock} disabled={!canManage} onChange={(e) => setRestock(e.target.checked)} data-testid="return-restock" />
            Reintegra il magazzino (solo casi fisicamente reintegrabili · admin/manager)
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={busy} data-testid="return-cancel">Annulla</Button>
          <Button className="rounded-full" onClick={submit} disabled={busy} data-testid="return-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registra reso"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
